/* eslint quotes: "off" */
import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import { AnyAction, Dispatch } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { handleErrorToast, handleSuccessToast } from 'src/components/molecules';
import { analytics, generateRandomIPAddress } from 'src/utils/analytics';
import { env } from 'src/utils/env';
import { initPendo, teardownPendo } from 'src/utils/pendo';
import {
  AUTHENTICATION_TYPE,
  AVAILABILITY,
  CASE_SETTINGS,
  LOGIN,
  LOGIN_2FA,
  SystemRolesName,
  TOUCH,
  handleAxiosError,
} from '../api';
import axios from '../axios';
import * as Types from '../types';
import { fetchFeatures } from './Feature.creators';
import { getAuthenticationAdmin } from './Organisation.creators';
import { closeSocket, createSocket } from './socket';

export const clearSession = () => async (dispatch: Dispatch<AnyAction>) => {
  dispatch({ type: Types.UserTypes.CLEAR_SESSION });
  closeSocket();
  teardownPendo();
};

export const getSocket = (token: string) => {
  return createSocket(token, clearSession);
};

const connectSocket = (token: string, dispatch: Dispatch<AnyAction>) => {
  const socket = createSocket(token, clearSession);

  socket.emit('join', { token });

  socket.on('chat', (message: Types.Message) => {
    dispatch({ type: Types.ReportTypes.MESSAGE, payload: message });
  });
};

const getUserId = (state: Types.State) => {
  if (
    state &&
    state.globalUsers &&
    state.globalUsers.currentUser &&
    state.globalUsers.currentUser.id
  ) {
    return state.globalUsers.currentUser.id;
  }

  throw Error('Current User is null');
};

export const updateAvailability =
  (available: boolean) =>
  async (dispatch: Dispatch<AnyAction>, getState: () => Types.State) => {
    try {
      const userId = getUserId(getState());

      await axios.put(AVAILABILITY, {
        user: userId,
        available,
      });

      dispatch({
        type: Types.UserTypes.UPDATE_AVAILABILITY,
        payload: available,
      });

      handleSuccessToast({ toastMessage: 'Updated availability successfully' });
    } catch (error) {
      handleErrorToast({ toastMessage: 'Couldnt update availability' });
      throw error;
    }
  };

export const updateTranslationEnabled =
  (translationEnabled: boolean) =>
  async (dispatch: Dispatch<AnyAction>, getState: () => Types.State) => {
    try {
      const userId = getUserId(getState());

      await axios.put(`${CASE_SETTINGS}/translationEnabled`, {
        user: userId,
        translationEnabled,
      });

      dispatch({
        type: Types.UserTypes.UPDATE_TRANSLATION_ENABLED,
        payload: translationEnabled,
      });

      handleSuccessToast({
        toastMessage: 'Updated translation setting successfully',
      });
    } catch (error) {
      handleErrorToast({ toastMessage: 'Couldnt update translation setting' });
      throw error;
    }
  };

export const editPersonalInformation =
  (user: Partial<Types.User>) => async (dispatch: Dispatch<AnyAction>) => {
    try {
      const result = await axios.put(LOGIN, user);
      dispatch({
        type: Types.UserTypes.LOGIN,
        payload: result.data.data.user,
      });
      handleSuccessToast({
        toastMessage: 'User information edited successfully',
      });
    } catch (error) {
      handleErrorToast({ toastMessage: 'Cant edit personal information' });
      throw error;
    }
  };

export const createSession =
  (username: string, password: string, segmentTracking?: boolean) =>
  async (dispatch: ThunkDispatch<Types.State, void, AnyAction>) => {
    try {
      const {
        data: { data: session },
      } = await axios.post(LOGIN, {
        username,
        password,
      });

      await dispatch(hydrate(session));
      Sentry.setUser({ id: session.account.user.id });
      if (segmentTracking) {
        analytics.identify(
          session.userHash,
          {},
          { context: { ip: generateRandomIPAddress() } },
        );
      }
    } catch (error) {
      throw handleAxiosError(
        error as AxiosError<{ errors: { message: string }[] }>,
      );
    }
  };

export const create2FASession =
  (
    username: string,
    password: string,
    code: string,
    segmentTracking?: boolean,
  ) =>
  async (dispatch: ThunkDispatch<Types.State, void, AnyAction>) => {
    try {
      const {
        data: { data: session },
      } = await axios.post(LOGIN_2FA, {
        username,
        password,
        code,
      });

      await dispatch(hydrate(session));
      Sentry.setUser({ id: session.account.user.id });
      if (segmentTracking) {
        analytics.identify(
          session.userHash,
          {},
          { context: { ip: generateRandomIPAddress() } },
        );
      }
    } catch (error) {
      throw handleAxiosError(
        error as AxiosError<{ errors: { message: string }[] }>,
      );
    }
  };

export const fetchSession =
  () => async (dispatch: ThunkDispatch<Types.State, void, AnyAction>) => {
    dispatch({
      type: Types.FetchingType.FETCHING,
      payload: true,
    });

    try {
      const {
        data: { data: session },
      } = await axios.get(LOGIN);

      await dispatch(hydrate(session));
    } catch (error) {
      handleAxiosError(error as AxiosError<{ errors: { message: string }[] }>);
    } finally {
      dispatch({
        type: Types.FetchingType.FETCHING_COMPLETED,
        payload: false,
      });
    }
  };

const hydrate =
  (session: Types.Session) =>
  async (
    dispatch: ThunkDispatch<Types.State, void, AnyAction>,
    getState: () => Types.State,
  ) => {
    connectSocket(session.token, dispatch);

    dispatch({
      type: Types.UserTypes.SESSION,
      payload: { session: session.token, hash: session.hash },
    });

    dispatch({
      type: Types.UserTypes.LOGIN,
      payload: session.account.user,
    });

    dispatch({
      type: Types.OrganisationTypes.FETCH,
      payload: session.account.user.organisation,
    });

    dispatch({
      type: Types.OrganisationTypes.FETCH_AUTH_TYPE,
      payload: {
        authenticationType:
          session.account.user.organisation.authenticationType,
      },
    });

    const { SUPER, ADMIN } = SystemRolesName;
    const systemRolesName = session.account.user.SystemRoles.map((x) => x.name);
    const hasOneOf = (ps: Array<SystemRolesName>) =>
      ps.some((x) => systemRolesName.includes(x));

    if (systemRolesName.length > 0) {
      await dispatch(fetchAuthenticationType(session.account.username));

      await dispatch(fetchFeatures());

      const isPendoEnabled = getState().features?.some(
        (f) => f.key === Types.FeatureFlag.FRONTEND_PENDO_TRACKING && f.enabled,
      );
      if (isPendoEnabled) {
        initPendo(
          env.REACT_APP_PENDO_APP_KEY,
          session.userHash,
          String(session.account.user.organisation.id),
        );
      }

      if (hasOneOf([SUPER, ADMIN])) {
        await dispatch(getAuthenticationAdmin());
      }

      dispatch({
        type: Types.LoginTypes.FLOW,
        payload: Types.LoginFlow.USERNAME,
      });

      dispatch({
        type: Types.LoginTypes.EMAIL,
        payload: '',
      });
    }
  };

export const fetchAuthenticationType =
  (email: string) => async (dispatch: Dispatch<AnyAction>) => {
    try {
      const {
        data: { data: payload },
      } = await axios.post(AUTHENTICATION_TYPE, {
        email,
      });
      const { authenticationType } = payload;
      dispatch({
        type: Types.OrganisationTypes.FETCH_AUTH_TYPE,
        payload: { authenticationType },
      });
    } catch (error) {
      handleAxiosError(error as AxiosError<{ errors: { message: string }[] }>);
    }
  };

export const touch = () => async (dispatch: Dispatch<AnyAction>) => {
  try {
    const response = await axios.post(TOUCH);
    const refreshedToken = response?.headers?.['x-session'];
    if (refreshedToken) {
      dispatch({
        type: Types.UserTypes.SESSION,
        payload: { session: refreshedToken },
      });
    }
    return response;
  } catch {
    return undefined;
  }
};

export const getExpiry = () => async () => {
  const response = await axios({
    method: 'get',
    url: TOUCH,
  });

  return (
    response &&
    response.data &&
    response.data.data &&
    response.data.data.updatedAt
  );
};
